import { Component, inject, TemplateRef, ViewChild } from '@angular/core';
import { AllowListAuthStore } from '../../../../shared/authorization/allow-list-auth.store';
import { PAGE_CONSTANTS } from '../../../../shared/constants/page.constants';
import { FormBuilder, FormsModule, ReactiveFormsModule } from '@angular/forms';
import { LookupService } from '../../../../services/lookup.service';
import { ErrorAlertService } from '../../../../shared/services/error-alert.service';
import { ListService } from '../../../../shared/services/list.service';
import { DateTimeFormatterService } from '../../../../shared/utils/date-time-formatter.service';
import { ListTableComponent } from '@ppl/ui';
import { TableRowActions } from '../../../../shared/interfaces/actions';
import { TableRowActionsModel } from '../../../../shared/interfaces/list.interface';
import { DialogConfig, DialogService } from '../../../../shared/services/dialog.service';
import { CommonModule } from '@angular/common';
import { NgZorroModule } from '../../../../shared/modules/ng-zorro/ng-zorro.module';
import { NzDatePickerModule } from 'ng-zorro-antd/date-picker';
import { LookupValueItem } from '../../../../interfaces/lookup.interface';
import { DatePickerModule } from 'primeng/datepicker';
import { UserService } from '../../../../services/user.service';
import { PurchaseAdjustmentFilterComponent } from '../purchase-filter/purchase-filter.component';
import { PurchaseAdjustVoidEntryComponent } from '../purchase-adjust-void-entry/purchase-adjust-void-entry.component';
import { AuthorizationDetailsComponent } from '../../authorizations/authorization-details/authorization-details.component';
import { SectionsService } from '../../../../services/sections.service';
import { CreatePurchaseRequestComponent } from '../create-purchase-request/create-purchase-request.component';

@Component({
    selector: 'ppl-purchase-adjustment',
    standalone: true,
    imports: [
        CommonModule,
        FormsModule,
        ReactiveFormsModule,
        NgZorroModule,
        NzDatePickerModule,
        DatePickerModule,
        ListTableComponent,
        PurchaseAdjustmentFilterComponent,
        PurchaseAdjustVoidEntryComponent,
        AuthorizationDetailsComponent,
        CreatePurchaseRequestComponent
    ],
    templateUrl: './purchase-adjustment.component.html',
    styleUrl: './purchase-adjustment.component.css'
})
export class PurchaseAdjustmentComponent {
    @ViewChild(ListTableComponent) childComponent!: ListTableComponent;
    @ViewChild(PurchaseAdjustmentFilterComponent) filterChildComponent!: PurchaseAdjustmentFilterComponent;
    @ViewChild('purchaseAdjustVoidDialog') purchaseAdjustVoidDialog!: TemplateRef<any>;
    @ViewChild('searchFilterWarningDialog') searchFilterWarningDialog!: TemplateRef<any>;
    @ViewChild('authDialog') authDialog!: TemplateRef<any>;
    // Passed to ppl-authorization-details as an input; never put into the shared selected lead.
    selectedAuthorizationId: any = null;
    @ViewChild('viewPurchaseRequestDialog') viewPurchaseRequestDialog!: TemplateRef<any>;

    private dialogSvc = inject(DialogService);
    private allowListStore = inject(AllowListAuthStore);

    // ─── Page Config ────────────────────────────────────────────────────────────
    pageId = PAGE_CONSTANTS.STAFF_PAYROLL_ADJUSTMENT;
    pageName = 'purchaseRequestAdjustmentEntry';
    pageTitle = 'Purchase Adjustment';
    dialogSource = 'payroll';
    // ────────────────────────────────────────────────────────────────────────────

    isReadOnly = false;
    actionButtons: TableRowActionsModel = new TableRowActions({ view: true });
    mode: 'add' | 'edit' | 'view' | 'actions' = 'view';
    viewPurchaseRequestData: any;
    viewPurchaseRequestDialogId: any;
    appliedFilters: any;
    selectedAction: any;
    programName!: string;
    processingStatusList: any;
    businessStatusList: any;
    purchases: any[] = [];
    serviceCodes: LookupValueItem[] = [];
    purchaseData: any = {};
    adjustVoidDialog: any;
    chequeNumbers: any[] = [];

    // Void removed — Purchase Adjustment supports Adjust only
    buttons = [
        {
            button_id: 'Adjust',
            button_label: 'Adjust',
            buttonAction: 'adjustPurchase',
            className: 'primary-btn-color'
        },
        {
            button_id: "Void",
            button_label: "Void",
            buttonAction: "voidPurchase",
            className: "primary-btn-color"
        },
        // Void All action — temporarily hidden, may be reintroduced later
        // {
        //     button_id: "VoidAll",
        //     button_label: "Void All",
        //     buttonAction: "voidAllPurchases",
        //     className: "primary-btn-color"
        // }
    ];

    cols = [
        { field: 'pplPurchaseId', header: 'PPL Purchase \nID', jsonpath: 'pplPurchaseId', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'submissionDate', header: 'Submission \nDate', jsonpath: 'submissionDate', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'consumerName', header: 'Participant \nName', jsonpath: 'consumerName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'participantPplId', header: 'Participant \nPPL ID', jsonpath: 'participantPplId', isDisplayedAtHeader: 'y', isSearchable: 'n', isClickable: true },
        { field: 'vendorName', header: 'Vendor \nName', jsonpath: 'vendorName', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'serviceCode', header: 'Service \nCode', jsonpath: 'serviceCode', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'authDesc', header: 'Service \nDescription', jsonpath: 'authDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'itemDesc', header: 'Item \nDescription', jsonpath: 'itemDesc', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'unit', header: 'Units', jsonpath: 'unit', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'price', header: 'Price', jsonpath: 'price', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'taxAmount', header: 'Tax \nAmount', jsonpath: 'taxAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'shippingAmount', header: 'Shipping \nAmount', jsonpath: 'shippingAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'totalAmount', header: 'Total \nAmount', jsonpath: 'totalAmount', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'businessStatus', header: 'Business \nStatus', jsonpath: 'businessStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'processingStatus', header: 'Processing \nStatus', jsonpath: 'processingStatus', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'orderNumber', header: 'Order \nNumber', jsonpath: 'orderNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'trackingNumber', header: 'Tracking \nNumber', jsonpath: 'trackingNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'tasAuthorizationDetailId', header: 'Authorization \nNumber', jsonpath: 'tasAuthorizationDetailId', isDisplayedAtHeader: 'y', isClickable: true, isSearchable: 'n' },
        // { field: 'sourceTypeLkp', header: 'Source', jsonpath: 'sourceTypeLkp', isDisplayedAtHeader: 'y', isSearchable: 'n' },
        { field: 'crmNumber', header: 'CRM \nNumber', jsonpath: 'crmNumber', isDisplayedAtHeader: 'y', isSearchable: 'n' }
    ];

    fields = [
        // no serviceDate for purchase
        { field: 'consumer', show: true },
        { field: 'consumerPplId', show: true },
        { field: 'serviceCode', show: true },
        // { field: 'crmNumber', show: true }, // CRM Number filter temporarily hidden
    ];

    keyMapping: { [key: string]: string } = {
        // no serviceDate for purchase
        Timeframe: 'submissionDate',
        consumer: 'consumerName',
        consumerPplId: 'consumerPplId',
        serviceCode: 'serviceCodeDetailId'
    };

    onViewUserClicked = (payload: any) => {
        this.userService.setViewUserEvent({
            userId: payload.userId,
            isConsumer: payload.isConsumer
        });
    };

    constructor(
        private listService: ListService,
        private fb: FormBuilder,
        private lookupService: LookupService,
        private dateTimeFormatterService: DateTimeFormatterService,
        private userService: UserService,
        private errorAlertService: ErrorAlertService,
        private sectionsService: SectionsService
    ) { }

    ngOnInit(): void {
        const selectedProgram = this.userService.getSelectedProgram();
        this.programName = selectedProgram?.program_code ?? '';
        this.businessStatusList = this.lookupService.getLookupValue('Business Status');
        this.processingStatusList = this.lookupService.getLookupValue('TS_App_S');
        this.serviceCodes = this.lookupService.getLookupValue('timesheet_filter_serviceCode');
        this.setPageAccess();
    }

    private setPageAccess(): void {
        const authConfig = this.allowListStore.getConfig();
        if (authConfig && !authConfig.pages[this.pageId]) {
            this.isReadOnly = true;
        }
    }

    // ─── Search & Data ───────────────────────────────────────────────────────────

    searchPurchases(filters: any, startingPageNumber = 1, currentPage = 0, isChequeLookUp = false, recordsPerPage = 20): void {
        if (startingPageNumber === 1) {
            this.purchases = [];
        }

        const processedPayload = this.buildRequestPayload(
            this.pageName,
            'purchaseRequestDetailId',
            filters,
            startingPageNumber,
            recordsPerPage
        );

        this.listService.getListData(processedPayload, 'claims').subscribe((data: any) => {
            const records = this.processData(data.responsedata);
            if (isChequeLookUp) {
                if (records?.length) {
                    this.chequeNumbers = Array.from(
                        new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber))
                    );
                }
            } else {
                this.purchases = startingPageNumber > 1
                    ? [...this.purchases, ...records]
                    : records;

                if (this.childComponent) {
                    this.childComponent.recordsPerPage = recordsPerPage;
                    this.childComponent.resetPagination(startingPageNumber, currentPage);
                }

                if (this.purchases?.length) {
                    this.chequeNumbers = Array.from(
                        new Set(records.filter((e: any) => e.chequeNumber).map((e: any) => e.chequeNumber))
                    );
                }
            }
        });
    }

    buildRequestPayload(pageName: string, orderBy: string, searchParams: [], startingPageNumber = 1, recordsPerPage = 20): any {
        if (!searchParams.length) return;

        return {
            pageName,
            programCode: this.programName,
            condition: 'and',
            recordsPerPage,
            startingPageNumber,
            searchParam: searchParams,
            orderByColumn: [],
            orderBy: ['desc']
        };
    }

    processData(data: any): any {
        if (data?.length > 0) {
            data.forEach((element: any) => {
                element.timeInTimeOut =
                    this.dateTimeFormatterService.convertTo12HourFormat(element.timeIn) +
                    ' - ' +
                    this.dateTimeFormatterService.convertTo12HourFormat(element.timeOut);

                element.businessStatus = this.businessStatusList
                    .filter((s: any) => s.lkpValueCode === element.businessStatusLkp)
                    .map((s: any) => s.lkpValueName);

                element.processingStatus = this.processingStatusList
                    .filter((s: any) => s.lkpValueCode === element.processingStatusLkp)
                    .map((s: any) => s.lkpValueName);

                const lookup = element.serviceCodeDetailId !== null && element.serviceCodeDetailId !== undefined
                    ? this.serviceCodes.find(
                        (sc: LookupValueItem) => sc.lkpValueCode === String(element.serviceCodeDetailId)
                    )
                    : undefined;
                if (lookup?.lkpValueName) {
                    element.serviceCode = lookup.lkpValueName;
                } else {
                    element.serviceCode = `${element.serviceCode ?? ''}: ${element.serviceDescription ?? ''}`;
                }
            });
        }
        return data;
    }

    // ─── Filter / Search Handler ─────────────────────────────────────────────────

    onSearch(event: any): void {
        const filters: any[] = [];

        if (event.Timeframe) {
            filters.push({ jsonPath: 'submissionDate', value: event.Timeframe, operator: 'greaterThan' });
        }

        if (event.submissionDate) {
            filters.push({
                jsonPath: 'submissionDate',
                value: this.dateTimeFormatterService.formatDateToString(event.submissionDate as Date),
                operator: 'Equal'
            });
        }

        if (event.pplPurchaseId?.trim()) {
            filters.push({ jsonPath: 'pplPurchaseId', value: event.pplPurchaseId.trim(), operator: 'Equal' });
        }

        if (event.consumer?.trim()) {
            filters.push({ jsonPath: 'consumerName', value: event.consumer.trim(), operator: 'equal' });
        }
        if (event.consumerPplId?.trim()) {
            filters.push({ jsonPath: 'consumerPplId', value: event.consumerPplId.trim(), operator: 'Equal' });
        }
        if (event.serviceCode) {
            filters.push({ jsonPath: 'serviceCodeDetailId', value: event.serviceCode, operator: 'Equal' });
        }
        // CRM Number filter temporarily hidden
        // if (event.crmNumber) {
        //     filters.push({ jsonPath: 'crmNumber', value: event.crmNumber, operator: 'Equal' });
        // }

        filters.push({ jsonPath: 'businessStatusLkp', value: 'P', operator: 'Equal' });
        filters.push({ jsonPath: "approval", value: false, operator: "Equal" });

        this.appliedFilters = filters;
        this.searchPurchases(filters, undefined, undefined, event.chequeLookUp, this.childComponent?.recordsPerPage ?? 20);
    }

    // ─── Grid Actions ────────────────────────────────────────────────────────────

    gridAction(event: any): void {
        const { button, selectedCheckBox } = event;

        const isAdjustAction = button.buttonAction === 'adjustPurchase';
        const isVoidAction = button.buttonAction === 'voidPurchase';

        if (selectedCheckBox?.length === 0) {
            this.errorAlertService.openModal('error', 'Error', 'Kindly select checkbox and then perform this action.');
            return;
        }

        if (isAdjustAction && selectedCheckBox.length > 1) {
            this.errorAlertService.openModal('error', 'Error', 'Adjust operation can be performed on a single purchase at a time.');
            return;
        }

        if (isVoidAction && selectedCheckBox.length > 1) {
            this.errorAlertService.openModal('error', 'Error', 'Void operation can be performed on a single purchase at a time.');
            return;
        }

        switch (button.buttonAction) {
            case 'adjustPurchase': {
                const ts = selectedCheckBox[0];
                this.purchaseData = {
                    purchaseRequestLineId: ts.purchaseRequestLineId,
                    participantId: ts.participantId,
                    submissionDate: ts.submissionDate,
                    serviceCodeDetailId: ts.serviceCodeDetailId,
                    serviceCodeLabel: ts.serviceCode,
                    authDesc: ts.authDesc,
                    tasAuthorizationDetailId: ts.tasAuthorizationDetailId,
                    unit: ts.unit,
                    price: ts.price,
                    crmNumber: ts.crmNumber,
                    taxAmount: ts.taxAmount,
                    shippingAmount: ts.shippingAmount,
                };
                break;
            }
            case 'voidPurchase': {
                const ts = selectedCheckBox[0];
                this.purchaseData = {
                    purchaseRequestLineId: ts.purchaseRequestLineId,
                    crmNumber: ts.crmNumber,
                    taxAmount: ts.taxAmount,
                    shippingAmount: ts.shippingAmount,
                };
                break;
            }
            // Void All action — temporarily hidden, may be reintroduced later
            // case 'voidAllPurchases': {
            //     if (selectedCheckBox.length > 0) {
            //         this.purchaseData = {
            //             purchaseRequestLineId: selectedCheckBox.map((entry: any) => entry.purchaseRequestLineId),
            //         }
            //     }
            //     break;
            // }
        }

        this.selectedAction = button;

        const dialogConfig: DialogConfig = {
            title: `Purchase ${button.button_label}`,
            size: 'medium',
            position: 'top',
            showClose: true,
            showButtons: false,
            styleClass: this.selectedAction?.buttonAction !== 'adjustPurchase' ? 'limit-dialog-width' : ''
        };

        this.adjustVoidDialog = this.dialogSvc.openDialog(
            this.purchaseAdjustVoidDialog,
            dialogConfig,
            () => this.handleCancel(),
            () => this.handleCancel()
        );
    }

    // ─── Dialog Helpers ──────────────────────────────────────────────────────────

    closeAdjustVoidDialog(): void {
        this.purchaseData = {};
        if (this.adjustVoidDialog) {
            this.dialogSvc.closeDialog(this.adjustVoidDialog);
            this.adjustVoidDialog = null;
        }
    }

    handleCancel(): void {
        this.closeAdjustVoidDialog();
    }

    purchaseAdjustedOrVoided(event: any): void {
        this.closeAdjustVoidDialog();
        this.searchPurchases(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, this.childComponent?.recordsPerPage ?? 20);
    }

    onPageLoad(event: any): void {
        this.searchPurchases(this.appliedFilters, event.startingPageNumber, event?.first ?? 0, false, event.rows ?? 20);
    }

    onCellClicked(event: any) {
        const col = event?.col;
        const rowData = event?.rowData;
        if (col?.field === 'tasAuthorizationDetailId' && rowData?.tasAuthorizationDetailId) {
            this.openAuthDialog(rowData);
        }
    }

    openAuthDialog(rowData: any) {
        this.selectedAuthorizationId = rowData.tasAuthorizationDetailId;
        const dialogConfig: DialogConfig = {
            title: `Auth Details for ${rowData.tasAuthorizationDetailId}`,
            size: 'xx-large',
            position: 'top',
            showClose: true,
            showButtons: false,
        };
        this.dialogSvc.openDialog(this.authDialog, dialogConfig, () => { }, () => { });
    }

    // ─── Row View Action ─────────────────────────────────────────────────────────

    onAction(event: any): void {
        if (event?.action === 'view') {
            const row = event?.data;
            this.viewPurchaseRequestData = {
                ...row,
                tasPurchaserequestDetailId: row?.tasPurchaserequestDetailId ?? row?.purchaseRequestDetailId
            };
            this.openViewPurchaseRequest();
        }
    }

    openViewPurchaseRequest(): void {
        this.mode = 'view';
        const dialogConfig: DialogConfig = {
            title: 'View Purchase Request',
            size: 'xx-large',
            position: 'top',
            showClose: true,
            showButtons: false
        };

        this.viewPurchaseRequestDialogId = this.dialogSvc.openDialog(
            this.viewPurchaseRequestDialog,
            dialogConfig,
            () => this.closeViewPurchaseRequestDialog(),
            () => this.closeViewPurchaseRequestDialog()
        );
    }

    closeViewPurchaseRequestDialog(): void {
        if (this.viewPurchaseRequestDialogId) {
            this.dialogSvc.closeDialog(this.viewPurchaseRequestDialogId);
            this.viewPurchaseRequestDialogId = null;
            this.viewPurchaseRequestData = null;
        }
    }

    handleViewPurchaseRequestCancel(event: any): void {
        if (event?.isCloseTriggeredByDialog) {
            this.closeViewPurchaseRequestDialog();
        }
    }

    // ─── Filter Validation Helpers ───────────────────────────────────────────────

    areArraysEqual(a: any[], b: any[]): boolean {
        if (!Array.isArray(a) || !Array.isArray(b)) return false;
        if (a.length !== b.length) return false;
        return a.every((val, i) => val === b[i]);
    }

    isFilterValid(purchaseFilterValues: any, searchCallbackFilterValues: any): boolean {
        const activeFilters = Object.keys(purchaseFilterValues).filter(key => {
            const value = purchaseFilterValues[key];
            if (value === null || value === undefined) return false;
            if (typeof value === 'string' && value.trim() === '') return false;
            if (Array.isArray(value) && value.length === 0) return false;
            return true;
        });

        const userAppliedFilters = searchCallbackFilterValues.filter(
            (filter: any) => !['businessStatusLkp', 'serviceDuration'].includes(filter.jsonPath)
        );

        if (activeFilters.length !== userAppliedFilters.length) return false;

        return Object.entries(purchaseFilterValues)
            .filter(([_, value]) => value !== null && value !== '')
            .every(([key, value]: [any, any]) => {
                const mappedKey = this.keyMapping[key] || key;
                if (Array.isArray(value) && value.length === 0) return true;

                let comparisonValue: any;
                if (mappedKey === 'serviceDate' || (key === 'submissionDate' && value instanceof Date)) {
                    comparisonValue = this.dateTimeFormatterService.formatDateToString(value as Date);
                } else if (mappedKey === 'serviceDuration') {
                    comparisonValue = value.substr(0, value.indexOf(' '));
                } else {
                    comparisonValue = value;
                }

                return searchCallbackFilterValues.some((filter: any) =>
                    filter.jsonPath === mappedKey &&
                    (Array.isArray(filter.value || filter.values)
                        ? this.areArraysEqual(filter.values, comparisonValue)
                        : typeof filter?.value === 'string'
                            ? filter?.value?.trim() === comparisonValue?.trim()
                            : filter?.value === comparisonValue)
                );
            });
    }
}
